import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { projectsAPI } from '../../services/api';
import { Project } from '../../types';
import ProjectModal from './ProjectModal';
import LoadingSpinner from '../Common/LoadingSpinner';

const Projects: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'updated'>('updated');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectCreated = (project: Project) => {
    setProjects(prev => [...prev, project]);
    setShowModal(false);
  };

  const handleProjectDeleted = async (projectId: number) => {
    if (!window.confirm(t('projects.confirmDelete', 'Are you sure you want to delete this project? This will also delete all associated targets and scans.'))) {
      return;
    }
    try {
      await projectsAPI.delete(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  };

  // Filter and sort projects
  const filteredProjects = projects
    .filter(project => {
      const matchesSearch = searchTerm === '' ||
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'updated':
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        default:
          return 0;
      }
    });

  // Calculate stats
  const totalTargets = projects.reduce((sum, p) => sum + (p.targets?.length || 0), 0);
  const totalScans = projects.reduce((sum, p) => sum + (p.scans?.length || 0), 0);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return t('common.today', 'Today');
    if (days === 1) return t('common.yesterday', 'Yesterday');
    if (days < 7) return t('common.daysAgo', '{{days}} days ago', { days });
    return date.toLocaleDateString();
  };

  // Styles
  const styles = {
    container: {
      backgroundColor: 'var(--color-background-secondary)',
      minHeight: '100vh',
      padding: '0'
    },
    header: {
      backgroundColor: 'var(--color-background)',
      borderBottom: '1px solid var(--color-border-primary)',
      padding: '2rem',
      boxShadow: '0 1px 3px var(--color-card-shadow)'
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      flexWrap: 'wrap' as const
    },
    headerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    title: {
      margin: '0',
      fontSize: '1.875rem',
      fontWeight: '700' as const,
      color: 'var(--color-text-primary)'
    },
    subtitle: {
      margin: '0.25rem 0 0',
      color: 'var(--color-text-tertiary)',
      fontSize: '0.875rem'
    },
    btnPrimary: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.5rem',
      backgroundColor: 'var(--color-primary-500)',
      color: 'var(--color-text-inverse)',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: '500' as const,
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    content: {
      padding: '2rem'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    statCard: {
      backgroundColor: 'var(--color-background)',
      borderRadius: '12px',
      border: '1px solid var(--color-border-primary)',
      padding: '1.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      boxShadow: '0 1px 3px var(--color-card-shadow)'
    },
    statIcon: (color: string) => ({
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      backgroundColor: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.25rem',
      flexShrink: 0
    }),
    statInfo: {
      flex: '1',
      display: 'flex',
      flexDirection: 'column' as const
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: '700' as const,
      color: 'var(--color-text-primary)'
    },
    statLabel: {
      fontSize: '0.75rem',
      fontWeight: '500' as const,
      color: 'var(--color-text-tertiary)',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em'
    },
    filtersCard: {
      backgroundColor: 'var(--color-background)',
      borderRadius: '12px',
      border: '1px solid var(--color-border-primary)',
      padding: '1.25rem',
      marginBottom: '1.5rem',
      boxShadow: '0 1px 3px var(--color-card-shadow)'
    },
    filtersRow: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
      flexWrap: 'wrap' as const
    },
    searchWrapper: {
      flex: '1',
      minWidth: '250px',
      position: 'relative' as const
    },
    searchIcon: {
      position: 'absolute' as const,
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: 'var(--color-text-muted)',
      fontSize: '0.875rem'
    },
    searchInput: {
      width: '100%',
      padding: '0.75rem 1rem 0.75rem 2.5rem',
      border: '1px solid var(--color-border-secondary)',
      borderRadius: '8px',
      fontSize: '0.875rem',
      outline: 'none',
      transition: 'all 0.2s'
    },
    sortSelect: {
      padding: '0.75rem 1rem',
      border: '1px solid var(--color-border-secondary)',
      borderRadius: '8px',
      fontSize: '0.875rem',
      backgroundColor: 'var(--color-background)',
      cursor: 'pointer',
      outline: 'none',
      minWidth: '150px'
    },
    activeFilters: {
      marginTop: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexWrap: 'wrap' as const
    },
    filterTag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.375rem 0.75rem',
      backgroundColor: 'var(--color-tint-blue)',
      color: 'var(--color-primary-700)',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: '500' as const
    },
    clearBtn: {
      background: 'none',
      border: 'none',
      color: 'inherit',
      cursor: 'pointer',
      padding: '0',
      fontSize: '0.875rem',
      display: 'flex',
      alignItems: 'center'
    },
    projectsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '1.5rem'
    },
    projectCard: {
      backgroundColor: 'var(--color-background)',
      borderRadius: '12px',
      border: '1px solid var(--color-border-primary)',
      overflow: 'hidden',
      boxShadow: '0 1px 3px var(--color-card-shadow)',
      transition: 'all 0.2s',
      cursor: 'pointer'
    },
    projectCardHeader: {
      padding: '1.25rem',
      borderBottom: '1px solid var(--color-background-tertiary)'
    },
    projectTitle: {
      margin: '0 0 0.5rem 0',
      fontSize: '1.125rem',
      fontWeight: '600' as const,
      color: 'var(--color-text-primary)'
    },
    projectDescription: {
      margin: '0',
      fontSize: '0.875rem',
      color: 'var(--color-text-tertiary)',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden'
    },
    projectStats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      padding: '1rem 1.25rem',
      backgroundColor: 'var(--color-background-secondary)',
      gap: '0.5rem'
    },
    projectStat: {
      textAlign: 'center' as const
    },
    projectStatValue: {
      fontSize: '1.25rem',
      fontWeight: '700' as const,
      color: 'var(--color-text-primary)',
      display: 'block'
    },
    projectStatLabel: {
      fontSize: '0.625rem',
      fontWeight: '500' as const,
      color: 'var(--color-text-tertiary)',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em'
    },
    projectFooter: {
      padding: '1rem 1.25rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    projectDate: {
      fontSize: '0.75rem',
      color: 'var(--color-text-muted)'
    },
    projectActions: {
      display: 'flex',
      gap: '0.5rem'
    },
    actionBtn: {
      padding: '0.5rem',
      backgroundColor: 'var(--color-background-tertiary)',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      color: 'var(--color-text-tertiary)',
      fontSize: '0.875rem',
      transition: 'all 0.2s'
    },
    deleteBtn: {
      padding: '0.5rem',
      backgroundColor: 'var(--color-tint-red)',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      color: 'var(--color-critical)',
      fontSize: '0.875rem',
      transition: 'all 0.2s'
    },
    emptyState: {
      backgroundColor: 'var(--color-background)',
      borderRadius: '12px',
      border: '1px solid var(--color-border-primary)',
      padding: '4rem 2rem',
      textAlign: 'center' as const,
      boxShadow: '0 1px 3px var(--color-card-shadow)'
    },
    emptyIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: 'var(--color-background-tertiary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
      fontSize: '2rem'
    },
    emptyTitle: {
      margin: '0 0 0.5rem 0',
      fontSize: '1.25rem',
      fontWeight: '600' as const,
      color: 'var(--color-text-primary)'
    },
    emptyText: {
      margin: '0 0 1.5rem 0',
      color: 'var(--color-text-tertiary)',
      fontSize: '0.875rem'
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      backgroundColor: 'var(--color-background-secondary)'
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '2rem'
      }}>
        <LoadingSpinner text={t('projects.loadingProjects', 'Loading projects...')} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerInfo}>
            <div>
              <h1 style={styles.title}>{t('projects.title', 'Projects')}</h1>
              <p style={styles.subtitle}>
                {t('projects.subtitle', 'Manage your security scanning projects and organize targets')}
              </p>
            </div>
          </div>
          <button
            style={styles.btnPrimary}
            onClick={() => setShowModal(true)}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-primary-500)';
            }}
          >
            <i className="fas fa-plus"></i> {t('projects.newProject', 'New Project')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon('var(--color-tint-purple)')}><i className="fas fa-folder-open" style={{ color: 'var(--color-purple-text)' }}></i></div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{projects.length}</span>
              <span style={styles.statLabel}>{t('dashboard.totalProjects', 'Total Projects')}</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon('var(--color-tint-green)')}><i className="fas fa-crosshairs" style={{ color: 'var(--color-success)' }}></i></div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{totalTargets}</span>
              <span style={styles.statLabel}>{t('dashboard.totalTargets', 'Total Targets')}</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon('var(--color-info-light)')}><i className="fas fa-search" style={{ color: 'var(--color-primary-500)' }}></i></div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{totalScans}</span>
              <span style={styles.statLabel}>{t('projects.totalScans', 'Total Scans')}</span>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon('var(--color-tint-yellow)')}><i className="fas fa-check-circle" style={{ color: 'var(--color-warning)' }}></i></div>
            <div style={styles.statInfo}>
              <span style={styles.statValue}>{projects.length}</span>
              <span style={styles.statLabel}>{t('projects.activeProjects', 'Active Projects')}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        {projects.length > 0 && (
          <div style={styles.filtersCard}>
            <div style={styles.filtersRow}>
              <div style={styles.searchWrapper}>
                <span style={styles.searchIcon}><i className="fas fa-search"></i></span>
                <input
                  type="text"
                  placeholder={t('projects.searchPlaceholder', 'Search projects by name or description...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={styles.searchInput}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-primary-500)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px var(--color-card-shadow)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'created' | 'updated')}
                style={styles.sortSelect}
              >
                <option value="updated">{t('common.recentlyUpdated', 'Recently Updated')}</option>
                <option value="created">{t('common.recentlyCreated', 'Recently Created')}</option>
                <option value="name">{t('common.nameAZ', 'Name (A-Z)')}</option>
              </select>
            </div>

            {searchTerm && (
              <div style={styles.activeFilters}>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', fontWeight: '500' }}>
                  {t('common.showingOf', 'Showing {{shown}} of {{total}} projects:', { shown: filteredProjects.length, total: projects.length })}
                </span>
                <span style={styles.filterTag}>
                  {t('common.search', 'Search')}: "{searchTerm}"
                  <button
                    onClick={() => setSearchTerm('')}
                    style={styles.clearBtn}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </span>
              </div>
            )}
          </div>
        )}

        {/* Projects Grid or Empty State */}
        {filteredProjects.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              <i className={projects.length === 0 ? 'fas fa-folder-open' : 'fas fa-search'} style={{ color: 'var(--color-text-muted)' }}></i>
            </div>
            <h2 style={styles.emptyTitle}>
              {projects.length === 0 ? t('projects.noProjectsYet', 'No Projects Yet') : t('projects.noMatchingProjects', 'No Matching Projects')}
            </h2>
            <p style={styles.emptyText}>
              {projects.length === 0
                ? t('projects.createFirstProjectHint', 'Create your first project to start organizing your security scans.')
                : t('projects.adjustSearchHint', 'Try adjusting your search term to find what you\'re looking for.')
              }
            </p>
            <button
              style={styles.btnPrimary}
              onClick={() => projects.length === 0 ? setShowModal(true) : setSearchTerm('')}
            >
              {projects.length === 0 ? <><i className="fas fa-plus"></i> {t('projects.createProject', 'Create Project')}</> : <><i className="fas fa-times"></i> {t('common.clearSearch', 'Clear Search')}</>}
            </button>
          </div>
        ) : (
          <div style={styles.projectsGrid}>
            {filteredProjects.map(project => (
              <div
                key={project.id}
                style={styles.projectCard}
                onClick={() => navigate(`/projects/${project.id}`)}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px var(--color-card-shadow)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={styles.projectCardHeader}>
                  <h3 style={styles.projectTitle}>{project.name}</h3>
                  <p style={styles.projectDescription}>
                    {project.description || t('projects.noDescription', 'No description provided')}
                  </p>
                </div>
                <div style={styles.projectStats}>
                  <div style={styles.projectStat}>
                    <span style={styles.projectStatValue}>{project.targets?.length || 0}</span>
                    <span style={styles.projectStatLabel}>{t('targets.title', 'Targets')}</span>
                  </div>
                  <div style={styles.projectStat}>
                    <span style={styles.projectStatValue}>{project.scans?.length || 0}</span>
                    <span style={styles.projectStatLabel}>{t('scans.title', 'Scans')}</span>
                  </div>
                  <div style={styles.projectStat}>
                    <span style={{
                      ...styles.projectStatValue,
                      color: 'var(--color-text-tertiary)'
                    }}>
                      -
                    </span>
                    <span style={styles.projectStatLabel}>{t('results.issues', 'Issues')}</span>
                  </div>
                </div>
                <div style={styles.projectFooter}>
                  <span style={styles.projectDate}>
                    {t('common.updated', 'Updated')} {formatDate(project.updated_at)}
                  </span>
                  <div style={styles.projectActions}>
                    <button
                      style={styles.actionBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${project.id}`);
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-border-primary)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-background-tertiary)';
                      }}
                      title={t('projects.viewProject', 'View Project')}
                    >
                      <i className="fas fa-eye" style={{ color: 'var(--color-text-tertiary)' }}></i>
                    </button>
                    <button
                      style={styles.deleteBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProjectDeleted(project.id);
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-tint-red)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-tint-red)';
                      }}
                      title={t('projects.deleteProject', 'Delete Project')}
                    >
                      <i className="fas fa-trash-alt" style={{ color: 'var(--color-critical)' }}></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <ProjectModal
          onClose={() => setShowModal(false)}
          onProjectCreated={handleProjectCreated}
        />
      )}
    </div>
  );
};

export default Projects;
